import React from 'react';
import cn from 'classnames';
import {Toggle} from '@rocket-ui/components';
import {TextInput} from 'features/registration/ui/components/TextInput';
import {RegistrationButton} from 'features/registration';
import {CloseIcon} from 'shared/icons';
import AddIcon from 'shared/assets/icons/add.svg?react';
import {
	RECEIPT_CLIENT_TYPE_OPTIONS,
	createEmptyReceiptService,
	formatReceiptDateDisplay,
	formatReceiptPrice,
	isReceiptServiceComplete,
	type ReceiptClientType,
	type ReceiptServiceItem,
} from '../../model/receiptForm';
import styles from './GenerateReceiptForm.module.css';

export type GenerateReceiptFormProps = {
	date: string;
	onDateChange: (value: string) => void;
	services: ReceiptServiceItem[];
	onServicesChange: (services: ReceiptServiceItem[]) => void;
	clientType: ReceiptClientType;
	onClientTypeChange: (value: ReceiptClientType) => void;
	isForeignOrganization: boolean;
	onForeignOrganizationChange: (value: boolean) => void;
	inn: string;
	onInnChange: (value: string) => void;
	organizationName: string;
	onOrganizationNameChange: (value: string) => void;
	onFormChange?: () => void;
};

export function GenerateReceiptForm({
	date,
	onDateChange,
	services,
	onServicesChange,
	clientType,
	onClientTypeChange,
	isForeignOrganization,
	onForeignOrganizationChange,
	inn,
	onInnChange,
	organizationName,
	onOrganizationNameChange,
	onFormChange,
}: GenerateReceiptFormProps): React.ReactElement {
	const [draftName, setDraftName] = React.useState('');
	const [draftPrice, setDraftPrice] = React.useState('');

	const canAdd = isReceiptServiceComplete({name: draftName, price: draftPrice});
	const showLegalFields = clientType === 'legal';
	const showOrganizationFields = showLegalFields && isForeignOrganization;

	const handleAddService = () => {
		if (!canAdd) {
			return;
		}

		onServicesChange([
			...services,
			{
				...createEmptyReceiptService(),
				name: draftName.trim(),
				price: draftPrice.trim(),
			},
		]);
		setDraftName('');
		setDraftPrice('');
	};

	const handleRemoveService = (id: string) => {
		onServicesChange(services.filter((service) => service.id !== id));
	};

	const handleInnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onInnChange(event.target.value.replace(/\D/g, '').slice(0, 12));
	};

	const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onDateChange(formatReceiptDateDisplay(event.target.value));
	};

	return (
		<div className={styles.root} onChange={onFormChange} onClick={onFormChange}>
			<section className={styles.section} aria-labelledby="receipt-data-title">
				<h2 id="receipt-data-title" className={styles.sectionTitle}>
					Данные
				</h2>
				<label className={styles.field}>
					<span className={styles.fieldLabel}>Дата</span>
					<TextInput
						name="receiptDate"
						className={styles.input}
						value={date}
						placeholder="01.01.2030"
						inputMode="numeric"
						autoComplete="off"
						onChange={handleDateChange}
						onClear={date ? () => onDateChange('') : undefined}
					/>
				</label>
			</section>

			<section className={styles.section} aria-labelledby="receipt-services-title">
				<h2 id="receipt-services-title" className={styles.sectionTitle}>
					Услуги
				</h2>

				{services.length > 0 ? (
					<ul className={styles.cards}>
						{services.map((service) => (
							<li key={service.id} className={styles.card}>
								<div className={styles.cardHeader}>
									<p className={styles.cardName}>{service.name}</p>
									<button
										type="button"
										className={styles.cardRemove}
										aria-label={`Удалить услугу: ${service.name}`}
										onClick={() => handleRemoveService(service.id)}
									>
										<CloseIcon className={styles.cardRemoveIcon} />
									</button>
								</div>
								<p className={styles.cardPrice}>{formatReceiptPrice(service.price)}</p>
							</li>
						))}
					</ul>
				) : null}

				<div className={styles.serviceBlock}>
					<label className={styles.field}>
						<span className={styles.fieldLabel}>Услуга</span>
						<TextInput
							name="receiptServiceDraft"
							className={styles.input}
							value={draftName}
							placeholder="Название услуги"
							autoComplete="off"
							onChange={(event) => setDraftName(event.target.value)}
							onClear={draftName ? () => setDraftName('') : undefined}
						/>
					</label>
					<label className={styles.field}>
						<span className={styles.fieldLabel}>Цена</span>
						<TextInput
							name="receiptPriceDraft"
							className={styles.input}
							value={draftPrice}
							placeholder="Цена услуги"
							inputMode="numeric"
							autoComplete="off"
							onChange={(event) => setDraftPrice(event.target.value.replace(/\D/g, ''))}
							onClear={draftPrice ? () => setDraftPrice('') : undefined}
						/>
					</label>
				</div>

				<RegistrationButton
					variant="primary"
					className={styles.addButton}
					onClick={handleAddService}
					disabled={!canAdd}
				>
					<span className={styles.addButtonContent}>
						<AddIcon className={styles.addIcon} aria-hidden />
						Добавить
					</span>
				</RegistrationButton>
			</section>

			<section className={styles.section} aria-labelledby="receipt-client-title">
				<h2 id="receipt-client-title" className={styles.sectionTitle}>
					Клиент
				</h2>
				<div className={styles.segmented} role="radiogroup" aria-label="Тип клиента">
					{RECEIPT_CLIENT_TYPE_OPTIONS.map((option) => {
						const selected = option.value === clientType;

						return (
							<button
								key={option.value}
								type="button"
								role="radio"
								aria-checked={selected}
								className={cn(styles.segment, selected && styles.segmentSelected)}
								onClick={() => onClientTypeChange(option.value)}
							>
								{option.label}
							</button>
						);
					})}
				</div>

				{showLegalFields ? (
					<div className={styles.legalFields}>
						<div className={styles.toggleRow}>
							<p className={styles.toggleLabel}>Иностранная организация</p>
							<Toggle value={isForeignOrganization} onChange={onForeignOrganizationChange} />
						</div>

						{showOrganizationFields ? (
							<>
								<label className={styles.field}>
									<span className={styles.fieldLabel}>ИНН</span>
									<TextInput
										name="receiptInn"
										className={styles.input}
										value={inn}
										placeholder="10-12 цифр"
										inputMode="numeric"
										autoComplete="off"
										onChange={handleInnChange}
										onClear={inn ? () => onInnChange('') : undefined}
									/>
								</label>

								<label className={styles.field}>
									<span className={styles.fieldLabel}>Организация</span>
									<TextInput
										name="receiptOrganization"
										className={styles.input}
										value={organizationName}
										placeholder="Название организации"
										autoComplete="organization"
										onChange={(event) => onOrganizationNameChange(event.target.value)}
										onClear={organizationName ? () => onOrganizationNameChange('') : undefined}
									/>
								</label>
							</>
						) : null}
					</div>
				) : null}
			</section>
		</div>
	);
}
